import * as Actions from '../actions/entityTimestampActions';
import EntityTimestamp from '../../entities/entityTimestamp';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type EntityTimestampAction = { 
    type: string; 
    entityTimestamp: EntityTimestamp | null; 
    entityTimestampId: string | EntityTimestamp; 
    partial: boolean; 
    lastFetch: number; 
    entityTimestamps: EntityTimestamp[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<EntityTimestamp> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {EntityTimestamp[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<EntityTimestamp> | null, firmId: string, userId: string, updates?: EntityTimestamp[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'entityTimestamp', 'entityTimestamps', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<EntityTimestamp>} data
 * @param {EntityTimestamp[]} [updates]
 * @returns {ArrayMap<EntityTimestamp>}
 */
function updateStorage(data: ArrayMap<EntityTimestamp>, updates?: EntityTimestamp[]): ArrayMap<EntityTimestamp> {
	if (logging) console.debug('⮂ entityTimestampReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {EntityTimestamp[]} [updates]
 * @returns {ArrayMap<EntityTimestamp>}
 */
function updateStorageNoReturn(updates?: EntityTimestamp[]): void {
	if (logging) console.debug('⮂ entityTimestampReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {EntityTimestamp[]} [data]
 * @returns {ArrayMap<EntityTimestamp>}
 */
function createState(data?: EntityTimestamp[] | ArrayMap<EntityTimestamp>): ArrayMap<EntityTimestamp> {
	if (!data) return ArrayMapRegistry.clone<EntityTimestamp>('entityTimestamp', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<EntityTimestamp>('entityTimestamp', data.map(e => e));
    else return ArrayMapRegistry.clone<EntityTimestamp>('entityTimestamp', data);
}

function entityTimestamps(state: ArrayMap<EntityTimestamp>, action: EntityTimestampAction): ArrayMap<EntityTimestamp> {
	if (!state) state = createState();

	let st: ArrayMap<EntityTimestamp>;

	switch (action.type) {
		// action.entityTimestamp - the new value of entityTimestamp with id action.id
		// action.entityTimestampId - id of the target, used if new value is null
		case Actions.ENTITYTIMESTAMP_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ entityTimestampReducer: FETCH_ONE_SUCCESS', action);

			if (action.entityTimestamp === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.entityTimestampId)) {
				st = state.clone();
				st.set(action.entityTimestamp);
				// state.clear();

				updateStorageNoReturn([action.entityTimestamp]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.entityTimestamps
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.entityTimestamps
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.entityTimestamps
			 !partial, !stateEmpty, fetchEmpty	-> D return entityTimestamps in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge entityTimestamps in state newer than those in action.entityTimestamps
		 */
		case Actions.ENTITYTIMESTAMP_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.entityTimestamps.length === 0;
			const fetched = action.entityTimestamps.map(entityTimestamp => new EntityTimestamp({...entityTimestamp, persisted: true}));

			if (logging) console.debug(`⮂ entityTimestampReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} entityTimestamps with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<EntityTimestamp> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: EntityTimestamp[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(entityTimestamp => {
					const current = st.get(entityTimestamp.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && entityTimestamp.status === 'D') {
							// Remove it from state
							toUpdate.push(entityTimestamp);
							st.delete(entityTimestamp);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (entityTimestamp.addedOn > current.addedOn) {
								toUpdate.push(entityTimestamp);
								st.set(entityTimestamp);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (entityTimestamp.status !== 'D') {
							toUpdate.push(entityTimestamp);
							st.set(entityTimestamp);
						}
						else if (logging) console.debug('⮂ entityTimestampReducer: partialMerge: skipping deleted entityTimestamp not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: empty: entityTimestamps', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ entityTimestampReducer: fullMerge: entityTimestamps', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localEntityTimestamp => {
                    // If this entityTimestamp has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localEntityTimestamp.persisted) {
                        const fetchedEntityTimestamp = st.get(localEntityTimestamp.id);
                        if (!fetchedEntityTimestamp || localEntityTimestamp.addedOn > fetchedEntityTimestamp.addedOn) st.set(localEntityTimestamp);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ENTITYTIMESTAMP_FETCH_ONE_FAILURE:
			console.error('ENTITYTIMESTAMP_FETCH_ONE_FAILURE', action.entityTimestampId, action.err);

			// No update to storage required
			return state;

		case Actions.ENTITYTIMESTAMP_FETCH_FAILURE:
			console.error('ENTITYTIMESTAMP_FETCH_FAILURE', action.entityTimestampId, action.err);

			// No update to storage required
			return state;

		case Actions.ENTITYTIMESTAMP_RESTORE: {
		    let entityTimestamp = new EntityTimestamp({...action.entityTimestamp});
            st = state.clone();

            const existing = st.get(entityTimestamp.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (entityTimestamp.addedOn >= existing.addedOn) {
                    st.set(entityTimestamp);
                    return updateStorage(st, [entityTimestamp]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (entityTimestamp.status === 'D') return state;

                st.set(entityTimestamp);
                return updateStorage(st, [entityTimestamp]);
            }
        }
            
		case Actions.ENTITYTIMESTAMP_SAVE: {
		    // console.debug('⮂ ENTITYTIMESTAMP_SAVE reducer', action.entityTimestamp?.id);
		    
			// clone the entityTimestamp
			let entityTimestamp = new EntityTimestamp({...action.entityTimestamp});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new entityTimestamp in the state
			// If this is a create, add to the queue
			const _entityTimestamp = st.get(entityTimestamp.id);

			if (!_entityTimestamp) st.set(entityTimestamp);
			else {
				if (entityTimestamp.addedOn >= _entityTimestamp.addedOn) st.set(entityTimestamp);
			}

			return updateStorage(st, [entityTimestamp]);
		}

		case Actions.ENTITYTIMESTAMP_SAVE_MANY: {
		    if (logging) console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));
		    
			// clone the entityTimestamps
			let toProcess = action.entityTimestamps.map(entityTimestamp => new EntityTimestamp(entityTimestamp));

			st = state.clone();
			// state.clear();

			const updates: EntityTimestamp[] = [];

			toProcess.forEach(entityTimestamp => {
				// Add or update the new entityTimestamp in the state
				// If this is a create, add to the queue
				const existing = st.get(entityTimestamp.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (entityTimestamp.status !== 'D') {
						st.push(entityTimestamp);
						updates.push(entityTimestamp);
					}
					else if (logging) console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY: skipping deleted entityTimestamp not in state');
				}
				else {
					if (entityTimestamp.addedOn >= existing.addedOn) {
						st.set(entityTimestamp);
						updates.push(entityTimestamp);
					}
					else if (logging) console.debug('⮂ Skipping, existing entityTimestamp had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ENTITYTIMESTAMP_REMOVE: {
			// clone the entityTimestamp
			const entityTimestamp = new EntityTimestamp({...action.entityTimestamp});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(entityTimestamp);

			return updateStorage(st, [entityTimestamp]);
		}

		case Actions.ENTITYTIMESTAMP_RESTORE_FAILURE:
		case Actions.ENTITYTIMESTAMP_SAVE_FAILURE: {
		    console.warn('ENTITYTIMESTAMP_SAVE_FAILURE reducer', action.entityTimestamp?.id);
		    if (!action.entityTimestamp?.id) {
				console.warn('ENTITYTIMESTAMP_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const entityTimestamp = ensure(EntityTimestamp, action.entityTimestamp);
			if (!entityTimestamp) {
				console.error('Could not find entityTimestamp after call to save');
				return state;
			}

			st = state.clone();

			const _entityTimestampRef = state.get(entityTimestamp.id);

			if (_entityTimestampRef && (_entityTimestampRef.addedOn === entityTimestamp.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _entityTimestamp = new EntityTimestamp({..._entityTimestampRef});
				_entityTimestamp.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_entityTimestamp.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('entityTimestamp', _entityTimestamp).then(r => {
						if (r.isErr()) console.error(`⮂ entityTimestampReducer: failed to save entityTimestamp with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ entityTimestampReducer: saved entityTimestamp with fatalError info');
					}).catch(err => console.error(`⮂ entityTimestampReducer: failed to save entityTimestamp with fatalError info: ${err.message}`));

				st.set(_entityTimestamp);
			}

			return updateStorage(st);
		}

		case Actions.ENTITYTIMESTAMP_SAVE_MANY_FAILURE: {
		    console.warn('ENTITYTIMESTAMP_SAVE_MANY_FAILURE reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ENTITYTIMESTAMP_RESTORE_SUCCESS:
		case Actions.ENTITYTIMESTAMP_SAVE_SUCCESS: {
			if (!action.entityTimestamp?.id) {
				console.warn('ENTITYTIMESTAMP_SAVE_SUCCESS reducer');
                return state;
			}

			const entityTimestampRef = state.get(action.entityTimestamp?.id);
			if (!entityTimestampRef) {
				console.warn('Could not find entityTimestamp after call to save');
                return state;
			}

			st = state.clone();

			if (action.entityTimestamp.addedOn >= entityTimestampRef.addedOn) {
				st.set(action.entityTimestamp);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const entityTimestamp = new EntityTimestamp({...entityTimestampRef, persisted: true});
				st.set(entityTimestamp);
			}

			return updateStorage(st, [action.entityTimestamp]);
		}

		case Actions.ENTITYTIMESTAMP_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY_SUCCESS reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const entityTimestamps: EntityTimestamp[] = [];

			action.entityTimestamps.forEach(e => {
				const entityTimestampRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!entityTimestampRef) console.warn('Could not find entityTimestamp after call to save');
				else if (e.addedOn >= entityTimestampRef.addedOn) {
				    st.set(e);
				    entityTimestamps.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const entityTimestamp = new EntityTimestamp({...entityTimestampRef, persisted: true});
					st.set(entityTimestamp);
					entityTimestamps.push(entityTimestamp);
				}
			});

			return updateStorage(st, entityTimestamps);
		}

		case Actions.ENTITYTIMESTAMP_REMOVE_FAILURE: {
		    console.warn('ENTITYTIMESTAMP_REMOVE_FAILURE action', action.entityTimestamp?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ENTITYTIMESTAMP_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ENTITYTIMESTAMP_RESTORE_MANY: {
			if (logging) console.debug('⮂ ENTITYTIMESTAMP_RESTORE_MANY reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));

			const toProcess = action.entityTimestamps.map(entityTimestamp => new EntityTimestamp({...entityTimestamp}));
			st = state.clone();

			const updates: EntityTimestamp[] = [];

			toProcess.forEach(entityTimestamp => {
				const existing = st.get(entityTimestamp.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (entityTimestamp.addedOn >= existing.addedOn) {
						st.set(entityTimestamp);
						updates.push(entityTimestamp);
					}
					else if (logging) console.debug('⮂ ENTITYTIMESTAMP_RESTORE_MANY: skipping older version of entityTimestamp');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (entityTimestamp.status !== 'D') {
						st.set(entityTimestamp);
						updates.push(entityTimestamp);
					}
					else if (logging) console.debug('⮂ ENTITYTIMESTAMP_RESTORE_MANY: skipping deleted entityTimestamp not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ENTITYTIMESTAMP_REMOVE_MANY: {
			if (logging) console.debug('⮂ ENTITYTIMESTAMP_REMOVE_MANY reducer', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));

			const toProcess = action.entityTimestamps.map(entityTimestamp => new EntityTimestamp({...entityTimestamp}));
			st = state.clone();

			toProcess.forEach(entityTimestamp => {
				st.delete(entityTimestamp);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default entityTimestamps;